/**
 * usePositionReferenceOptions — player_position reference values grouped by
 * tactical position group (for optgroup-style rendering), built on top of
 * `useReferenceValues`. Mirrors `withCurrentValuePreserved`'s never-drop
 * rule but keeps the `group` bucketing the position pickers need.
 *
 * Always fetches with includeInactive: true — cheap (one small category),
 * and it's what lets a deprecated/unknown position code stay visible and
 * selected on an existing record without a separate "edit mode" fetch.
 */

import { useTranslation } from "react-i18next";
import {
  resolveReferenceLabel,
  useReferenceValues,
} from "./useReferenceValues";
import type { PositionGroup } from "../lib/referenceDataApi";

export const POSITION_GROUP_ORDER: PositionGroup[] = [
  "goalkeeper",
  "defense",
  "midfield",
  "attack",
  "other",
];

export interface PositionOption {
  code: string;
  label: string;
  isDeprecated: boolean;
}

/**
 * `currentCode` is the form/filter's current value — included even if
 * deactivated or entirely unknown to the reference collection, so it's
 * never silently dropped from the picker.
 */
export function usePositionReferenceOptions(currentCode?: string | null) {
  const { i18n } = useTranslation();
  const { data, isLoading } = useReferenceValues("player_position", {
    includeInactive: true,
  });
  const list = data ?? [];
  const active = list.filter((v) => v.is_active);

  const grouped: Record<PositionGroup, PositionOption[]> = {
    goalkeeper: [],
    defense: [],
    midfield: [],
    attack: [],
    other: [],
  };

  for (const value of active) {
    const group = value.group ?? "other";
    grouped[group].push({
      code: value.code,
      label: resolveReferenceLabel(value, i18n.language),
      isDeprecated: false,
    });
  }

  if (currentCode && !active.some((v) => v.code === currentCode)) {
    const known = list.find((v) => v.code === currentCode);
    const group = known?.group ?? "other";
    grouped[group].push({
      code: currentCode,
      label: known ? resolveReferenceLabel(known, i18n.language) : currentCode,
      isDeprecated: true,
    });
  }

  const labelFor = (code: string | undefined | null): string => {
    if (!code) return "";
    const known = list.find((v) => v.code === code);
    return known ? resolveReferenceLabel(known, i18n.language) : code;
  };

  return {
    grouped,
    groupOrder: POSITION_GROUP_ORDER,
    activeCodes: active.map((v) => v.code),
    labelFor,
    isLoading,
  };
}
